import { useEffect, useRef, useState } from 'react';
import { useSocket } from '@conductor/hooks/useSocket';
import TigerSQLite from '@shared/utils/mobile/tigerSqlite';

const GOOGLE_API_KEY = process.env.REACT_APP_GOOGLE_MAP_API_KEY;
const LS_KEY = 'ultima_ubicacion_gps';
const SQLITE_TABLE = 'ubicacion_gps_cache';
const SQLITE_CAMPOS = [
    { name: 'id', type: 'INTEGER PRIMARY KEY' },
    { name: 'lat', type: 'REAL' },
    { name: 'lng', type: 'REAL' },
    { name: 'nombre', type: 'TEXT' },
];

// Variable en memoria para preservar el nombre entre updates de GPS
// Inicializar con el último nombre conocido de localStorage (sesión anterior)
let cachedNombre = '';
try { cachedNombre = JSON.parse(localStorage.getItem('ultima_ubicacion_gps') || '{}').nombre || ''; } catch {}

// Guardar coords en localStorage + SQLite, preservando el nombre
// Encadena las operaciones SQLite para evitar race conditions
let sqliteWriteChain = Promise.resolve();
function guardarCoords(lat, lng) {
    // Si no tenemos nombre (offline, sin geocoding), preservar el que ya estaba
    let nombre = cachedNombre;
    if (!nombre) {
        try {
            const prev = JSON.parse(localStorage.getItem(LS_KEY) || '{}');
            nombre = prev.nombre || '';
        } catch {}
    }
    const data = { lat, lng, nombre };
    try { localStorage.setItem(LS_KEY, JSON.stringify(data)); } catch {}
    // SQLite: create → delete → create → insert, encadenado secuencialmente
    sqliteWriteChain = sqliteWriteChain
        .then(() => TigerSQLite.modifyTable('create', SQLITE_TABLE, SQLITE_CAMPOS))
        .then(() => TigerSQLite.modifyTable('delete', SQLITE_TABLE))
        .then(() => TigerSQLite.modifyTable('create', SQLITE_TABLE, SQLITE_CAMPOS))
        .then(() => TigerSQLite.insert(SQLITE_TABLE, { lat, lng, nombre }))
        .catch(() => {});
}

// Reverse geocoding con debounce — solo cada 30s como máximo
let lastGeocode = 0;
async function reverseGeocode(lat, lng) {
    const now = Date.now();
    if (now - lastGeocode < 30000 || !GOOGLE_API_KEY) return;
    lastGeocode = now;
    try {
        const res = await fetch(
            `https://maps.googleapis.com/maps/api/geocode/json?latlng=${lat},${lng}&key=${GOOGLE_API_KEY}&language=es&result_type=locality|sublocality|administrative_area_level_1`
        );
        const data = await res.json();
        if (data.results?.[0]?.formatted_address) {
            cachedNombre = data.results[0].formatted_address;
            // Guardar con nombre actualizado
            guardarCoords(lat, lng);
        }
    } catch {}
}

/**
 * Hook para iniciar el GPS watch desde el momento en que se monta.
 * Guarda coords en localStorage + SQLite + reverse geocode.
 * Se usa en PageMapChofer para arrancar el GPS lo antes posible.
 */
export const useGPSWatch = () => {
    const [ubicacionGPS, setUbicacionGPS] = useState({});

    useEffect(() => {
        const onPosition = (lat, lng) => {
            console.log('[DEBUG][useGPSWatch] Posición obtenida:', lat, lng);
            setUbicacionGPS({ lat, lng });
            guardarCoords(lat, lng);
            reverseGeocode(lat, lng);
        };

        // 1. Bridge nativo (principal en dispositivo)
        // Solo setear si no hay un modal que ya lo haya tomado
        const setTigerCallback = () => {
            window.__gpsWatchCallback = (latitude, longitude) => {
                onPosition(+latitude, +longitude);
            };
            // Si no hay tigerGetLocation activo de un modal, usar el nuestro
            if (!window.tigerGetLocation) {
                window.tigerGetLocation = window.__gpsWatchCallback;
            }
        };
        setTigerCallback();

        // Pedir GPS al bridge nativo
        try {
            if (/iPhone|iPad/i.test(navigator.userAgent)) {
                window.webkit?.messageHandlers?.JphIOS?.postMessage({ action: 'tigerStartLocation', params: {} });
            } else if (/Android/i.test(navigator.userAgent) && typeof Jph !== 'undefined') {
                Jph.tigerStartLocation();
            }
            console.log('[DEBUG][useGPSWatch] tigerStartLocation llamado');
        } catch (e) {
            console.warn('[DEBUG][useGPSWatch] Error llamando tigerStartLocation:', e);
        }

        // 2. Browser geolocation (fallback)
        let watchId = null;
        if (navigator.geolocation) {
            watchId = navigator.geolocation.watchPosition(
                (posicion) => onPosition(posicion.coords.latitude, posicion.coords.longitude),
                (err) => console.warn(`[DEBUG][useGPSWatch] Browser GPS error: ${err.message} (code: ${err.code})`),
                { enableHighAccuracy: false, timeout: 10000, maximumAge: 120000 }
            );
        }

        // 3. Restaurar tigerGetLocation cuando un modal lo libera
        const interval = setInterval(() => {
            if (!window.tigerGetLocation && window.__gpsWatchCallback) {
                window.tigerGetLocation = window.__gpsWatchCallback;
            }
        }, 2000);

        return () => {
            if (watchId != null) navigator.geolocation.clearWatch(watchId);
            clearInterval(interval);
            if (window.tigerGetLocation === window.__gpsWatchCallback) {
                delete window.tigerGetLocation;
            }
            delete window.__gpsWatchCallback;
        };
    }, []);

    return ubicacionGPS;
};

/**
 * Hook para socket de viaje. Recibe ubicacionGPS desde el padre
 * y la combina con updates del socket.
 */
export const useSocketViaje = (ubicacionGPS, ordenInicial) => {
    const [ubicacionVehiculo, setUbicacionVehiculo] = useState(ubicacionGPS || {});
    const orden = useRef(ordenInicial);

    // Sincronizar cuando el GPS watch actualiza
    useEffect(() => {
        if (ubicacionGPS?.lat && ubicacionGPS?.lng) {
            setUbicacionVehiculo(ubicacionGPS);
        }
    }, [ubicacionGPS]);

    useSocket([
        {
            event: "ubicacion_vehiculo",
            handler: ({ data }) => {
                if (data.lat && data.lng) {
                    setUbicacionVehiculo({ lat: data.lat, lng: data.lng });
                }
            },
        },
        {
            event: "orden_actualizado",
            handler: ({ data }) => {
                if (data?.orden != null) orden.current = data.orden;
            }
        }
    ]);

    return { ubicacionVehiculo, orden };
};